/**
 * ============================================
 * LEVEL SEARCH COMPONENT
 * ============================================
 * 
 * Searchable list of all levels with their stats
 * Shows: Level name, Completion %, Quit Rate %, Avg Time, Entries
 */

import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp, Gamepad2, FlaskConical, Users, Square, Swords } from 'lucide-react';
import { useLevelSettings } from '@/context/LevelSettingsContext';
import type { LevelStats } from '@/hooks/useAnalytics';
import { CardCountryButton } from '@/components/common/CardCountryFilter';

interface LevelSearchProps {
  levelStats: LevelStats[];
  campaignProgress: Array<{ level: string; completion: number; quitRate: number }>;
  isDark: boolean;
}

type SortField = 'level' | 'completion' | 'quitRate' | 'avgTime' | 'entries';
type SortDirection = 'asc' | 'desc';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  singleplayer: <Gamepad2 className="h-3.5 w-3.5 text-emerald-400" />,
  labz: <FlaskConical className="h-3.5 w-3.5 text-purple-400" />,
  ugc: <Users className="h-3.5 w-3.5 text-blue-400" />,
  twod: <Square className="h-3.5 w-3.5 text-amber-400" />,
  pvp: <Swords className="h-3.5 w-3.5 text-red-400" />,
};

export function LevelSearch({ levelStats, campaignProgress, isDark }: LevelSearchProps) {
  const { getEffectiveCategory } = useLevelSettings();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('level');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [expandedLevel, setExpandedLevel] = useState<string | null>(null);

  // Merge level stats with campaign progress
  const mergedData = useMemo(() => {
    const campaignMap = new Map(campaignProgress.map(c => [c.level, c]));
    
    return levelStats.map(stat => {
      const campaign = campaignMap.get(stat.level);
      return {
        ...stat,
        completion: campaign?.completion ?? 0,
        category: getEffectiveCategory(stat.level)
      };
    });
  }, [levelStats, campaignProgress, getEffectiveCategory]);

  // Filter and sort
  const filteredData = useMemo(() => {
    let data = mergedData.filter(stat => 
      stat.level.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort
    data.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'level':
          comparison = a.level.localeCompare(b.level);
          break;
        case 'completion':
          comparison = a.completion - b.completion;
          break;
        case 'quitRate':
          comparison = a.quitRate - b.quitRate;
          break;
        case 'avgTime':
          comparison = (a.avgTime || 0) - (b.avgTime || 0);
          break;
        case 'entries':
          comparison = a.entries - b.entries;
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return data;
  }, [mergedData, searchQuery, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const borderColor = isDark ? 'border-gray-700' : 'border-gray-200';
  const textColor = isDark ? 'text-gray-100' : 'text-gray-900';
  const mutedText = isDark ? 'text-gray-400' : 'text-gray-500';
  const inputBg = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300';
  const headerBg = isDark ? 'bg-gray-800' : 'bg-gray-100';
  const rowHover = isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100';

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-3 w-3" />
      : <ChevronDown className="h-3 w-3" />;
  };

  return (
    <div className={`rounded-xl border ${borderColor}`}>
      {/* Search Header */}
      <div className={`p-4 ${headerBg} border-b ${borderColor} rounded-t-xl`}>
        <div className="flex items-center justify-between gap-4">
          <h3 className={`text-sm font-semibold ${textColor}`}>
            All Levels ({filteredData.length})
          </h3>
          <CardCountryButton />
          <div className="relative flex-1 max-w-md">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${mutedText}`} />
            <input
              type="text"
              placeholder="Search levels..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg border ${inputBg} ${textColor} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
            />
          </div>
        </div>
      </div>

      {/* Table (clipped here, not on the card, so the header dropdown isn't cut off) */}
      <div className="overflow-x-auto rounded-b-xl">
        <table className="w-full">
          <thead>
            <tr className={`${headerBg} border-b ${borderColor}`}>
              <th 
                className={`px-4 py-3 text-left text-xs font-semibold ${mutedText} uppercase tracking-wider cursor-pointer hover:text-white`}
                onClick={() => handleSort('level')}
              >
                <div className="flex items-center gap-1">
                  Level <SortIcon field="level" />
                </div>
              </th>
              <th 
                className={`px-4 py-3 text-right text-xs font-semibold ${mutedText} uppercase tracking-wider cursor-pointer hover:text-white`}
                onClick={() => handleSort('completion')}
              >
                <div className="flex items-center justify-end gap-1">
                  Completion <SortIcon field="completion" />
                </div>
              </th>
              <th 
                className={`px-4 py-3 text-right text-xs font-semibold ${mutedText} uppercase tracking-wider cursor-pointer hover:text-white`}
                onClick={() => handleSort('quitRate')}
              >
                <div className="flex items-center justify-end gap-1">
                  Quit Rate <SortIcon field="quitRate" />
                </div>
              </th>
              <th 
                className={`px-4 py-3 text-right text-xs font-semibold ${mutedText} uppercase tracking-wider cursor-pointer hover:text-white`}
                onClick={() => handleSort('avgTime')}
              >
                <div className="flex items-center justify-end gap-1">
                  Avg Time <SortIcon field="avgTime" />
                </div>
              </th>
              <th 
                className={`px-4 py-3 text-right text-xs font-semibold ${mutedText} uppercase tracking-wider cursor-pointer hover:text-white`}
                onClick={() => handleSort('entries')}
              >
                <div className="flex items-center justify-end gap-1">
                  Players <SortIcon field="entries" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {filteredData.slice(0, 50).map(stat => {
              const categoryIcon = CATEGORY_ICONS[stat.category];
              
              return (
                <tr 
                  key={stat.level}
                  className={`${rowHover} cursor-pointer transition-colors`}
                  onClick={() => setExpandedLevel(expandedLevel === stat.level ? null : stat.level)}
                >
                  <td className={`px-4 py-3 ${textColor}`}>
                    <div className="flex items-center gap-2">
                      {categoryIcon}
                      <span className="font-mono text-sm">{stat.level}</span>
                    </div>
                  </td>
                  <td className={`px-4 py-3 text-right`}>
                    <span className={`font-medium ${stat.completion >= 70 ? 'text-emerald-400' : stat.completion >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {stat.completion.toFixed(1)}%
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-right`}>
                    <span className={`font-medium ${stat.quitRate <= 30 ? 'text-emerald-400' : stat.quitRate <= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {stat.quitRate.toFixed(1)}%
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-right ${mutedText}`}>
                    {stat.avgTime ? `${stat.avgTime.toFixed(1)} min` : '-'}
                  </td>
                  <td className={`px-4 py-3 text-right ${mutedText}`}>
                    {stat.entries}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredData.length > 50 && (
          <div className={`p-4 text-center ${mutedText} text-sm border-t ${borderColor}`}>
            Showing 50 of {filteredData.length} levels. Use search to find specific levels.
          </div>
        )}
        
        {filteredData.length === 0 && (
          <div className={`p-8 text-center ${mutedText}`}>
            No levels found matching "{searchQuery}"
          </div>
        )}
      </div>
    </div>
  );
}
